/**
 * Partnership Cache Utility
 * 
 * Manages localStorage caching for partnership data per apartment to avoid
 * expensive N queries when loading favorites list.
 * 
 * Each apartment's partnership status is cached independently, allowing
 * selective cache invalidation when specific partnerships change.
 */

import { Partnership } from './partnerships';

interface CachedPartnership {
  partnership: Partnership | null;
  hasPartner: boolean;
  timestamp: number;
}

interface CachedPartnerships {
  [apartmentId: string]: CachedPartnership;
}

const CACHE_KEY_PREFIX = 'partnerships_cache_';
const DEFAULT_MAX_AGE = 5 * 60 * 1000; // 5 minutes

/**
 * Get the cache key for a specific user
 */
function getCacheKey(userId: string): string {
  return `${CACHE_KEY_PREFIX}${userId}`;
}

/**
 * Check if a cache timestamp is still valid
 * Exported for use by other cache utilities (e.g., partnerships)
 */
export function isCacheValid(timestamp: number, maxAge: number = DEFAULT_MAX_AGE): boolean {
  const now = Date.now();
  return (now - timestamp) < maxAge;
}

/**
 * Retrieve cached partnership data for a specific apartment
 * Returns null if cache doesn't exist, is invalid, or corrupted
 */
export function getCachedPartnership(userId: string, apartmentId: string): CachedPartnership | null {
  try {
    const cacheKey = getCacheKey(userId);
    const cached = localStorage.getItem(cacheKey);
    
    if (!cached) return null;
    
    const parsed: CachedPartnerships = JSON.parse(cached);
    
    // Validate cache structure
    if (!parsed || typeof parsed !== 'object') {
      localStorage.removeItem(cacheKey);
      return null;
    }
    
    const apartmentCache = parsed[apartmentId];
    if (!apartmentCache) return null;
    
    // Validate apartment cache structure
    if (typeof apartmentCache.timestamp !== 'number' ||
        typeof apartmentCache.hasPartner !== 'boolean') {
      // Invalid structure - remove this apartment's cache
      delete parsed[apartmentId];
      localStorage.setItem(cacheKey, JSON.stringify(parsed));
      return null;
    }
    
    // Check if cache is still valid (not expired)
    if (!isCacheValid(apartmentCache.timestamp)) {
      // Cache expired - remove it
      delete parsed[apartmentId];
      localStorage.setItem(cacheKey, JSON.stringify(parsed));
      return null;
    }
    
    return apartmentCache;
  } catch (error) {
    // Cache corruption or parse error - clear it
    console.error('Error reading partnership cache:', error);
    try {
      localStorage.removeItem(getCacheKey(userId));
    } catch {
      // Ignore errors during cleanup
    }
    return null;
  }
}

/**
 * Store partnership data in cache for a specific apartment
 */
export function setCachedPartnership(
  userId: string,
  apartmentId: string,
  partnership: Partnership | null,
  hasPartner: boolean
): void {
  try {
    const cacheKey = getCacheKey(userId);
    let cached: CachedPartnerships = {};
    
    // Load existing cache
    const existing = localStorage.getItem(cacheKey);
    if (existing) {
      try {
        cached = JSON.parse(existing);
        if (!cached || typeof cached !== 'object') {
          cached = {};
        }
      } catch {
        // Parse error - start fresh
        cached = {};
      }
    }
    
    // Update cache for this apartment
    cached[apartmentId] = {
      partnership,
      hasPartner,
      timestamp: Date.now(),
    };
    
    localStorage.setItem(cacheKey, JSON.stringify(cached));
  } catch (error) {
    // Storage quota exceeded or other error - gracefully degrade
    console.warn('Failed to cache partnership data:', error);
    // Don't throw - caching is a performance optimization, not critical
  }
}

/**
 * Invalidate (clear) the partnership cache for a specific apartment
 * If apartmentId is not provided, clears all partnership caches for the user
 */
export function invalidatePartnershipCache(userId: string, apartmentId?: string): void {
  try {
    const cacheKey = getCacheKey(userId);
    
    if (!apartmentId) {
      // Clear all partnerships for this user
      localStorage.removeItem(cacheKey);
      return;
    }
    
    // Clear specific apartment's partnership cache
    const existing = localStorage.getItem(cacheKey);
    if (existing) {
      try {
        const cached: CachedPartnerships = JSON.parse(existing);
        if (cached && typeof cached === 'object') {
          delete cached[apartmentId];
          localStorage.setItem(cacheKey, JSON.stringify(cached));
        }
      } catch {
        // Parse error - clear entire cache
        localStorage.removeItem(cacheKey);
      }
    }
  } catch (error) {
    console.warn('Failed to invalidate partnership cache:', error);
  }
}

/**
 * Clear all partnership caches (useful for cleanup or logout)
 */
export function clearAllPartnershipCaches(): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CACHE_KEY_PREFIX)) {
        keys.push(key);
      }
    }
    keys.forEach(key => localStorage.removeItem(key));
  } catch (error) {
    console.warn('Failed to clear all partnership caches:', error);
  }
}

